// Vyhledávač hráčů — sjednocená databáze, řaditelné sloupce, presety, volitelné atributy.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Funnel, Plus, Check, CaretUp, CaretDown, Database, ClockCounterClockwise } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import ContextArticles from "../components/ContextArticles";
import Finders from "../components/timing/Finders";
import { useSharedPool } from "../lib/timing-pool-store";
import { DnaPoolButton } from "../components/DnaPoolButton";
import { useAuth } from "../contexts/AuthContext";
import { findPlayers, addToPool, removePoolByFpl } from "../lib/lab-api";

const POSITIONS = ["GKP", "DEF", "MID", "FWD"];
const DEFAULT_FILTERS = { position: "", team: "", price_min: 3.5, price_max: 16, points_min: 0, minutes_min: 0, ownership_max: 100, sort: "points", order: "desc" };

// Všechny dostupné sloupce (klíč, hlavička, sort klíč, formát)
const COLUMNS = {
    price: { label: "Cena 26/27", sort: "price", fmt: (r) => `£${r.price?.toFixed(1)}`, cls: "text-fa-yellow" },
    points: { label: "Body 25/26", sort: "points", fmt: (r) => r.total_points },
    value: { label: "Value", sort: "value", fmt: (r) => r.value, cls: "text-fa-mint" },
    fdr_easy: { label: "FDR lehcí", sort: "fdr_easy", fmt: (r) => r.fdr_easy_ppg ?? "—" },
    fdr_hard: { label: "FDR těžcí", sort: "fdr_hard", fmt: (r) => r.fdr_hard_ppg ?? "—" },
    hauls: { label: "Hauly", sort: "hauls", fmt: (r) => r.hauls ?? "—", cls: "text-fa-yellow" },
    consistency: { label: "Konzist.", sort: "consistency", fmt: (r) => r.consistency ?? "—" },
    explosiveness: { label: "Výbušnost", sort: "explosiveness", fmt: (r) => r.explosiveness ?? "—" },
    minutes: { label: "Minuty", sort: "minutes", fmt: (r) => r.minutes },
    ga: { label: "G+A", sort: "goals", fmt: (r) => (r.goals ?? 0) + (r.assists ?? 0) },
    bonus: { label: "Bonusy", sort: "bonus", fmt: (r) => r.bonus },
    clean_sheets: { label: "Č. konta", sort: "clean_sheets", fmt: (r) => r.clean_sheets },
    ownership: { label: "Vl. %", sort: "ownership", fmt: (r) => `${r.ownership_pct}%` },
    haul_rate: { label: "Haul %", sort: "haul_rate", fmt: (r) => r.haul_rate != null ? `${r.haul_rate}%` : "—" },
    fdr_diff: { label: "FDR diff", sort: "fdr_diff", fmt: (r) => r.fdr_diff ?? "—" },
    preseason: { label: "Příprava", sort: "preseason", fmt: (r) => r.preseason_share != null ? `${r.preseason_share}% (#${r.preseason_rank})` : "—", cls: "text-fa-sky" },
    defcon: { label: "DefCon %", sort: "defcon", fmt: (r) => r.defcon_prob_pct != null ? `${r.defcon_prob_pct}%` : "—", cls: "text-[#FF8800]" },
    defcon90: { label: "DC/90", sort: "defcon_per90", fmt: (r) => r.defcon_per90 != null ? `${r.defcon_per90} (${r.defcon_total ?? 0})` : "—" },
};
const DEFAULT_COLS = ["price", "points", "value", "fdr_easy", "fdr_hard", "hauls", "consistency", "explosiveness"];
const EXTRA_COLS = ["minutes", "ga", "bonus", "clean_sheets", "ownership", "haul_rate", "fdr_diff", "preseason", "defcon", "defcon90"];

// Předdefinované žebříčky
const PRESETS = [
    { key: "explosive", label: "Nejvýbušnější", filters: { minutes_min: 900, sort: "explosiveness", order: "desc" }, cols: ["price", "points", "explosiveness", "hauls", "haul_rate"] },
    { key: "consistent", label: "Konzistentní jistoty", filters: { minutes_min: 1500, sort: "consistency", order: "desc" }, cols: ["price", "points", "consistency", "minutes", "value"] },
    { key: "hauls", label: "Haulmasteři", filters: { minutes_min: 900, sort: "hauls", order: "desc" }, cols: ["price", "points", "hauls", "haul_rate", "explosiveness"] },
    { key: "hardhitters", label: "Kanonýři těžkých zápasů", filters: { minutes_min: 900, sort: "fdr_hard", order: "desc" }, cols: ["price", "points", "fdr_hard", "fdr_easy", "fdr_diff"] },
    { key: "value", label: "Top value", filters: { points_min: 60, sort: "value", order: "desc" }, cols: ["price", "points", "value", "minutes", "ownership"] },
    { key: "avoid", label: "Nekupujte", filters: { price_min: 5.5, minutes_min: 900, sort: "value", order: "asc" }, cols: ["price", "points", "value", "minutes", "ownership"] },
    { key: "differentials", label: "Diferenciály", filters: { ownership_max: 5, points_min: 80, sort: "points", order: "desc" }, cols: ["price", "points", "value", "ownership", "explosiveness"] },
    { key: "preseason", label: "Rozjetí v přípravě", filters: { sort: "preseason", order: "desc" }, cols: ["price", "points", "preseason", "minutes", "ownership"] },
    { key: "defcon", label: "DefCon lovci", filters: { minutes_min: 900, sort: "defcon", order: "desc" }, cols: ["price", "points", "defcon", "defcon90", "clean_sheets", "value"] },
];

export default function PlayerFinder() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const mode = searchParams.get("mode") === "historic" ? "historic" : "db";
    const sharedPool = useSharedPool();
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [cols, setCols] = useState(DEFAULT_COLS);
    const [preset, setPreset] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const debRef = useRef(null);

    const search = useCallback((f) => {
        setLoading(true);
        const params = { ...f, limit: 60 };
        if (!params.position) delete params.position;
        if (!params.team) delete params.team;
        findPlayers(params)
            .then(setData)
            .catch(() => toast.error("Vyhledávání selhalo"))
            .finally(() => setLoading(false));
    }, []);

    // Debounce — méně dotazů, rychlejší pocit
    useEffect(() => {
        clearTimeout(debRef.current);
        debRef.current = setTimeout(() => search(filters), 350);
        return () => clearTimeout(debRef.current);
    }, [filters, search]);

    const set = (k, v) => { setPreset(null); setFilters((f) => ({ ...f, [k]: v })); };

    const applyPreset = (p) => {
        setPreset(p.key);
        setFilters({ ...DEFAULT_FILTERS, ...p.filters });
        setCols(p.cols);
    };

    const sortBy = (colKey) => {
        const s = COLUMNS[colKey].sort;
        setFilters((f) => ({ ...f, sort: s, order: f.sort === s && f.order === "desc" ? "asc" : "desc" }));
    };

    const togglePool = async (p) => {
        if (!user) { toast.error("Pro práci s poolem se přihlas"); return; }
        try {
            if (p.in_pool) {
                await removePoolByFpl(p.fpl_id);
                toast.success(`${p.name} vyřazen z poolu`);
            } else {
                await addToPool({ fpl_id: p.fpl_id, status: "shortlist" });
                toast.success(`${p.name} → Shortlist`);
            }
            setData((d) => d && { ...d, results: d.results.map((x) => x.fpl_id === p.fpl_id ? { ...x, in_pool: !p.in_pool } : x) });
        } catch (e) { toast.error(e?.response?.data?.detail || "Akce selhala"); }
    };

    const SortArrow = ({ colKey }) => {
        if (filters.sort !== COLUMNS[colKey].sort) return <CaretDown size={9} className="opacity-25" />;
        return filters.order === "desc" ? <CaretDown size={9} className="text-fa-mint" /> : <CaretUp size={9} className="text-fa-mint" />;
    };

    return (
        <AppShell>
            <div className="space-y-5" data-testid="player-finder-page">
                <header className="border-2 border-fa-mint/40 bg-gradient-to-br from-fa-mint/10 via-transparent to-fa-sky/5 p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Funnel size={11} weight="fill" /> Manager Lab · Analyze
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Vyhledávač hráčů</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Jedna databáze, všechny naše atributy — FDR lehcí/těžcí, hauly, value, konzistence, výbušnost i <span className="text-[#FF8800]">DefCon %</span> (šance na body za defenzivní aktivitu — odhad z DC/90 minulé sezóny, práh DEF 10 / MID+FWD 12 akcí). Klikni na sloupec pro řazení ↑↓, vybrané hráče pošli rovnou na Shortlist.
                    </p>
                </header>

                {/* Režimy: databáze 26/27 × historické žebříčky */}
                <div className="flex gap-1" data-testid="finder-modes">
                    <button type="button" onClick={() => setSearchParams({})} data-testid="finder-mode-db"
                            className={`font-mono text-[11px] uppercase tracking-widest px-4 py-2 flex items-center gap-1.5 ${mode === "db" ? "bg-fa-mint text-black font-bold" : "border border-white/15 text-white/60 hover:text-white"}`}>
                        <Database size={12} /> Databáze 26/27
                    </button>
                    <button type="button" onClick={() => setSearchParams({ mode: "historic" })} data-testid="finder-mode-historic"
                            className={`font-mono text-[11px] uppercase tracking-widest px-4 py-2 flex items-center gap-1.5 ${mode === "historic" ? "bg-fa-mint text-black font-bold" : "border border-white/15 text-white/60 hover:text-white"}`}>
                        <ClockCounterClockwise size={12} /> Historické žebříčky
                    </button>
                </div>

                {mode === "historic" && <Finders lang="cs" sharedPool={sharedPool} />}
                {mode === "db" && (<>

                {/* Předdefinované žebříčky */}
                <div className="flex gap-1.5 flex-wrap" data-testid="finder-presets">
                    {PRESETS.map((p) => (
                        <button key={p.key} type="button" onClick={() => applyPreset(p)}
                                data-testid={`preset-${p.key}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${preset === p.key ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60 hover:text-fa-mint hover:border-fa-mint/40"}`}>
                            {p.label}
                        </button>
                    ))}
                    {preset && (
                        <button type="button" onClick={() => { setPreset(null); setFilters(DEFAULT_FILTERS); setCols(DEFAULT_COLS); }}
                                data-testid="preset-clear"
                                className="font-mono text-[10px] px-3 py-1.5 text-white/40 hover:text-white">× zrušit</button>
                    )}
                </div>

                {/* Filtry */}
                <div className="border border-white/10 bg-[#0d0d0d] p-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="finder-filters">
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Pozice</div>
                        <div className="flex gap-1 flex-wrap">
                            <button type="button" onClick={() => set("position", "")} data-testid="pf-pos-all"
                                    className={`font-mono text-[10px] px-2 py-1 border ${!filters.position ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>VŠE</button>
                            {POSITIONS.map((p) => (
                                <button key={p} type="button" onClick={() => set("position", p)} data-testid={`pf-pos-${p}`}
                                        className={`font-mono text-[10px] px-2 py-1 border ${filters.position === p ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>{p}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Tým</div>
                        <select value={filters.team} onChange={(e) => set("team", e.target.value)} data-testid="pf-team"
                                className="w-full bg-black border border-white/15 font-mono text-[11px] px-2 py-1.5 text-white">
                            <option value="">Všechny týmy</option>
                            {(data?.teams || []).map((tm) => <option key={tm} value={tm}>{tm}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Cena £{filters.price_min} – £{filters.price_max}</div>
                        <div className="flex items-center gap-2">
                            <input type="range" min={3.5} max={16} step={0.5} value={filters.price_min}
                                   onChange={(e) => set("price_min", Math.min(Number(e.target.value), filters.price_max))}
                                   data-testid="pf-price-min" className="w-full accent-[#B8FFDD]" />
                            <input type="range" min={3.5} max={16} step={0.5} value={filters.price_max}
                                   onChange={(e) => set("price_max", Math.max(Number(e.target.value), filters.price_min))}
                                   data-testid="pf-price-max" className="w-full accent-[#B8FFDD]" />
                        </div>
                    </div>
                    <div className="flex items-end">
                        <div className="font-mono text-[10px] text-white/50" data-testid="pf-count">
                            {loading ? "Hledám…" : data ? `${data.total_matching} hráčů odpovídá` : ""}
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Min. bodů 25/26: {filters.points_min}</div>
                        <input type="range" min={0} max={200} step={10} value={filters.points_min}
                               onChange={(e) => set("points_min", Number(e.target.value))}
                               data-testid="pf-points-min" className="w-full accent-[#B8FFDD]" />
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Min. minut: {filters.minutes_min}</div>
                        <input type="range" min={0} max={3000} step={100} value={filters.minutes_min}
                               onChange={(e) => set("minutes_min", Number(e.target.value))}
                               data-testid="pf-minutes-min" className="w-full accent-[#B8FFDD]" />
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Max. vlastnictví: {filters.ownership_max}%</div>
                        <input type="range" min={1} max={100} step={1} value={filters.ownership_max}
                               onChange={(e) => set("ownership_max", Number(e.target.value))}
                               data-testid="pf-ownership-max" className="w-full accent-[#B8FFDD]" />
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase text-white/45 mb-1.5">Přidat atributy</div>
                        <div className="flex gap-1 flex-wrap">
                            {EXTRA_COLS.map((c) => (
                                <button key={c} type="button"
                                        onClick={() => setCols((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c])}
                                        data-testid={`pf-col-${c}`}
                                        className={`font-mono text-[9px] px-1.5 py-0.5 border ${cols.includes(c) ? "border-fa-sky text-fa-sky" : "border-white/10 text-white/35"}`}>
                                    {COLUMNS[c].label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Výsledky */}
                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Hráč</th>
                                <th className="px-3 py-2">Tým · Poz</th>
                                {cols.map((c) => (
                                    <th key={c} className="px-3 py-2 text-right cursor-pointer select-none hover:text-white"
                                        onClick={() => sortBy(c)} data-testid={`pf-th-${c}`}>
                                        <span className="inline-flex items-center gap-0.5">{COLUMNS[c].label} <SortArrow colKey={c} /></span>
                                    </th>
                                ))}
                                <th className="px-3 py-2 text-right">Pool</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.results || []).map((p, i) => (
                                <tr key={p.fpl_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`pf-row-${i}`}>
                                    <td className="px-3 py-2 font-mono text-[10px] text-white/40">{i + 1}</td>
                                    <td className="px-3 py-2">
                                        <Link to={`/players/${p.fpl_id}`} data-testid={`pf-profile-${i}`}
                                              className="font-display font-black text-sm hover:text-fa-mint transition-colors">{p.name}</Link>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-white/55">
                                        {p.team} · {p.position}
                                        {p.new_coach && <span className="ml-1.5 font-mono text-[8px] uppercase text-[#FF8800] border border-[#FF8800]/50 px-1" title="Nový trenér — hráč nemusí být správně analyzovatelný (jiný herní styl, čeká se, co trenér přinese)">Nový trenér</span>}
                                    </td>
                                    {cols.map((c) => (
                                        <td key={c} className={`px-3 py-2 text-right font-mono text-[11px] tabular-nums ${COLUMNS[c].cls || ""}`}>
                                            {COLUMNS[c].fmt(p)}
                                        </td>
                                    ))}
                                    <td className="px-3 py-2 text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                        <DnaPoolButton player={p} compact />
                                        {p.in_pool ? (
                                            <button type="button" onClick={() => togglePool(p)} data-testid={`pf-remove-${i}`}
                                                    title="Vyřadit z poolu"
                                                    className="font-mono text-[9px] text-fa-mint hover:text-red-300 transition-colors flex items-center justify-end gap-1 group/pool">
                                                <Check size={11} weight="bold" className="group-hover/pool:hidden" />
                                                <span className="group-hover/pool:hidden">v poolu</span>
                                                <span className="hidden group-hover/pool:inline">vyřadit ×</span>
                                            </button>
                                        ) : (
                                            <button type="button" onClick={() => togglePool(p)} data-testid={`pf-add-${i}`}
                                                    className="font-mono text-[9px] uppercase border border-fa-mint/40 text-fa-mint px-2 py-1 hover:bg-fa-mint hover:text-black flex items-center gap-1">
                                                <Plus size={10} weight="bold" /> Shortlist
                                            </button>
                                        )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && data && data.results.length === 0 && (
                                <tr><td colSpan={cols.length + 4} className="px-3 py-6 text-center font-mono text-[11px] text-white/40">Žádný hráč neodpovídá kritériím</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <p className="font-mono text-[9px] text-white/35">
                    FDR lehcí/těžcí = PPG proti FDR 1–2 / 4–5 (zmrazené FDR v čase) · Hauly = zápasy s 10+ body · Konzistence a výbušnost z 6+ sezón historie. Hráči bez historie mají „—".
                </p>
                </>)}
                <ContextArticles category="players" />
            </div>
        </AppShell>
    );
}
